import React from 'react';
import styled from 'styled-components';
import { motion, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FiMail, FiPhone, FiMapPin } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import SocialIcons from './SocialIcons';

// Dark premium footer background
const Container = styled.footer`
  background: linear-gradient(
    180deg,
    ${({ theme }) => theme.colors.primary} 0%,
    ${({ theme }) => theme.colors.primary}dd 100%
  );
  color: ${({ theme }) => theme.colors.textLight};
  padding: ${({ theme }) => theme.layout.radiusLg} ${({ theme }) => theme.layout.radiusMd};
  margin-top: ${({ theme }) => theme.layout.radiusLg};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    inset-inline: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent 0%,
      ${({ theme }) => theme.colors.secondary}50 50%,
      transparent 100%
    );
  }
`;

const Wrapper = styled(motion.div)`
  max-width: ${({ theme }) => theme.layout.maxWidth};
  margin: 0 auto;
  display: grid;
  gap: ${({ theme }) => theme.layout.radiusLg};

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${({ theme }) => theme.layout.radiusLg};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: 1.5fr 1fr 1fr 1fr;
    gap: ${({ theme }) => theme.layout.radiusLg};
  }
`;

// Brand Section
const BrandSection = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.layout.radiusMd};
`;

const BrandLogo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.layout.radiusSm};
  margin-bottom: ${({ theme }) => theme.layout.radiusSm};
`;

const LogoImage = styled.img`
  width: 56px;
  height: 56px;
  object-fit: contain;
  border-radius: ${({ theme }) => theme.layout.radiusSm};
  filter: brightness(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
`;

const BrandName = styled.h3`
  font-size: 1.4rem;
  font-weight: 700;
  font-family: ${({ theme }) => theme.fonts.en};
  color: ${({ theme }) => theme.colors.textLight};
  margin: 0;
  letter-spacing: 0.05em;
  text-transform: uppercase;
`;

const BrandDescription = styled.p`
  font-size: 0.95rem;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textLight}dd;
  margin: 0;
  max-width: 320px;
`;

// Section Column
const Section = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.layout.radiusSm};
`;

const SectionTitle = styled.h4`
  font-size: 1.1rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textLight};
  margin: 0 0 ${({ theme }) => theme.layout.radiusSm} 0;
  font-family: ${({ theme }) => theme.fonts.ar};
  position: relative;
  padding-bottom: ${({ theme }) => theme.layout.radiusSm};

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    inset-inline-start: 0;
    width: 40px;
    height: 2px;
    background: ${({ theme }) => theme.colors.secondary};
    border-radius: 2px;
  }
`;

const LinkList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.layout.radiusSm};
`;

const FooterLink = styled(motion(Link))`
  color: ${({ theme }) => theme.colors.textLight}cc;
  text-decoration: none;
  font-size: 0.95rem;
  font-family: ${({ theme }) => theme.fonts.ar};
  transition: all 0.3s ease;
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  width: fit-content;

  &::after {
    content: '';
    position: absolute;
    bottom: -2px;
    inset-inline-start: 0;
    width: 0;
    height: 1.5px;
    background: ${({ theme }) => theme.colors.secondary};
    transition: width 0.3s ease;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.textLight};
    transform: translateX(-4px);
  }

  &:hover::after {
    width: 100%;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.secondary};
    outline-offset: 4px;
    border-radius: 4px;
  }
`;

// Contact Info
const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.layout.radiusMd};
`;

const ContactItem = styled(motion.a)`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.layout.radiusSm};
  color: ${({ theme }) => theme.colors.textLight}cc;
  text-decoration: none;
  font-size: 0.95rem;
  font-family: ${({ theme }) => theme.fonts.ar};
  transition: all 0.3s ease;
  line-height: 1.6;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: ${({ theme }) => theme.colors.secondary};
    transition: transform 0.3s ease;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.textLight};
    transform: translateX(-4px);
  }

  &:hover svg {
    transform: scale(1.1);
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.secondary};
    outline-offset: 4px;
    border-radius: 4px;
  }
`;

const ContactText = styled.span`
  flex: 1;
`;

// Address item (non-clickable)
const AddressItem = styled(motion.div)`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.layout.radiusSm};
  color: ${({ theme }) => theme.colors.textLight}cc;
  font-size: 0.95rem;
  font-family: ${({ theme }) => theme.fonts.ar};
  line-height: 1.6;

  svg {
    flex-shrink: 0;
    margin-top: 2px;
    color: ${({ theme }) => theme.colors.secondary};
  }
`;

const AddressText = styled.span`
  flex: 1;
`;

// Bottom Bar
const BottomBar = styled(motion.div)`
  max-width: ${({ theme }) => theme.layout.maxWidth};
  margin: ${({ theme }) => theme.layout.radiusLg} auto 0;
  padding-top: ${({ theme }) => theme.layout.radiusLg};
  border-top: 1px solid ${({ theme }) => theme.colors.textLight}20;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.layout.radiusMd};
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colors.textLight}aa;
  font-family: ${({ theme }) => theme.fonts.ar};

  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    text-align: center;
    gap: ${({ theme }) => theme.layout.radiusSm};
  }
`;

const Copyright = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
`;

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.4, 0, 0.2, 1],
    },
  },
};

const Footer = () => {
  const { t } = useTranslation();
  const prefersReducedMotion = useReducedMotion();

  const quickLinks = [
    { path: '/about', label: t('nav.about') },
    { path: '/projects', label: t('nav.projects') },
    { path: '/services', label: t('nav.services') },
    { path: '/contact', label: t('nav.contact') },
    { path: '/privacy-policy', label: t('footer.privacy_policy', { defaultValue: 'سياسة الخصوصية' }) },
  ];

  return (
    <Container>
      <Wrapper
        variants={prefersReducedMotion ? {} : containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: '-100px' }}
      >
        {/* Brand Section */}
        <BrandSection variants={prefersReducedMotion ? {} : itemVariants}>
          <BrandLogo>
            <LogoImage src="/Macintosh-HD.png" alt="AMER COMPANY Logo" />
            <BrandName>AMER COMPANY</BrandName>
          </BrandLogo>
          <BrandDescription>
            {t('footer.description', {
              defaultValue: 'منذ 1993 نطوّر مشاريع عقارية وسياحية وصناعية تربط القاهرة بالإسكندرية وإسطنبول، مع التزام طويل الأمد تجاه المستثمرين والشركاء.',
            })}
          </BrandDescription>
          <SocialIcons />
        </BrandSection>

        {/* Quick Links */}
        <Section variants={prefersReducedMotion ? {} : itemVariants}>
          <SectionTitle>{t('footer.quick_links', { defaultValue: 'روابط سريعة' })}</SectionTitle>
          <LinkList>
            {quickLinks.map((link, index) => (
              <FooterLink
                key={link.path}
                to={link.path}
                variants={prefersReducedMotion ? {} : itemVariants}
                whileHover={prefersReducedMotion ? {} : { x: -4 }}
                transition={{ duration: 0.2 }}
              >
                {link.label}
              </FooterLink>
            ))}
          </LinkList>
        </Section>

        {/* Contact Info */}
        <Section variants={prefersReducedMotion ? {} : itemVariants}>
          <SectionTitle>{t('footer.contact', { defaultValue: 'اتصل بنا' })}</SectionTitle>
          <ContactList>
            <ContactItem
              href="tel:+904445974"
              variants={prefersReducedMotion ? {} : itemVariants}
              whileHover={prefersReducedMotion ? {} : { x: -4 }}
            >
              <FiPhone />
              <ContactText>+90 444 59 74</ContactText>
            </ContactItem>
            <ContactItem
              href="https://wa.me/904445974"
              target="_blank"
              rel="noopener noreferrer"
              variants={prefersReducedMotion ? {} : itemVariants}
              whileHover={prefersReducedMotion ? {} : { x: -4 }}
            >
              <FaWhatsapp />
              <ContactText>WhatsApp</ContactText>
            </ContactItem>
            <ContactItem
              href="mailto:info@amercompany.com"
              variants={prefersReducedMotion ? {} : itemVariants}
              whileHover={prefersReducedMotion ? {} : { x: -4 }}
            >
              <FiMail />
              <ContactText>info@amercompany.com</ContactText>
            </ContactItem>
            <AddressItem variants={prefersReducedMotion ? {} : itemVariants}>
              <FiMapPin />
              <AddressText>
                {t('footer.address_istanbul', {
                  defaultValue: 'باشاك شهير - إسطنبول - تركيا',
                })}
              </AddressText>
            </AddressItem>
            <AddressItem variants={prefersReducedMotion ? {} : itemVariants}>
              <FiMapPin />
              <AddressText>
                KAYABAŞI MAH. ULUBATLI HASAN CAD. GİRİŞ KAYAŞEHİR D BLOK NO: 2 H İÇ KAPI NO: 82
              </AddressText>
            </AddressItem>
          </ContactList>
        </Section>

        {/* Company Info */}
        <Section variants={prefersReducedMotion ? {} : itemVariants}>
          <SectionTitle>{t('footer.company_info', { defaultValue: 'معلومات الشركة' })}</SectionTitle>
          <LinkList>
            <AddressItem variants={prefersReducedMotion ? {} : itemVariants}>
              <ContactText>
                {t('footer.trade_registry', {
                  defaultValue: 'السجل التجاري: 0680884182',
                })}
              </ContactText>
            </AddressItem>
            <AddressItem variants={prefersReducedMotion ? {} : itemVariants}>
              <ContactText>
                {t('footer.chamber', {
                  defaultValue: 'غرفة تجارة إسطنبول',
                })}
              </ContactText>
            </AddressItem>
          </LinkList>
        </Section>
      </Wrapper>

      {/* Bottom Bar */}
      <BottomBar
        variants={prefersReducedMotion ? {} : itemVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
      >
        <Copyright>
          © {new Date().getFullYear()} {t('footer.company_name', { defaultValue: 'Amer Investment & Development' })}
        </Copyright>
        <span>{t('footer.all_rights', { defaultValue: 'جميع الحقوق محفوظة' })}</span>
      </BottomBar>
    </Container>
  );
};

export default Footer;
